// Can 73: a change to who belongs to a World (invite, resend, withdraw, revoke, restore, remove for good; P4: what a
// member may do, and Move), stored
// by the World as a card before anything changes (docs/world-members-design.md 5.3-5.4). The founder approves it
// only on this card (gate HG-WORLD-GOVERN-02, card capability world-members-card-v1): every line is the World's,
// shown as stored, the "When you approve" line included. The card adds only the change's key and the expiry.
// Can 47: the Desk (apps/web memberChangeClient, gateInstancesClient) and Pocket (apps/mobile cloudConversationGate) show it
// with these same rows; Pocket reads the stored change from the approval the World hands it (pocketCloudDoors.waitingApprovals).
import * as Schema from "effect/Schema";

const text = Schema.String.check(Schema.isNonEmpty());
const change = Schema.Struct({
  // v2 (sent only to a Desk declaring world-member-change-v2) is v1 plus refusalCode; nothing else is new.
  schemaVersion: Schema.Literals(["world-member-change-v1", "world-member-change-v2"]),
  changeKey: text,
  kind: Schema.Literals([
    "invite-link",
    "invite-email",
    "resend",
    "withdraw",
    "revoke",
    "restore",
    "remove",
    // Can 73, P4: what a member may do, and a Move's removal from this World (its card names both Worlds).
    "permissions",
    "move",
    // Can 73, P5b: paid seats released from the World's next renewal (its card names no person).
    "release-seats",
  ]),
  title: text,
  status: Schema.Literals(["proposed", "dispatching", "applied", "refused", "expired", "unknown"]),
  proposedAt: Schema.Number,
  expiresAt: Schema.Number,
  // Each line is exactly a label and its words; a line carrying more would show less than the World stored.
  lines: Schema.Array(Schema.Tuple([Schema.String, Schema.String])),
  digest: text,
  // The exact message an email invitation or resend sends, its link shown as "[the personal invitation link]".
  email: Schema.optionalKey(
    Schema.Struct({ to: text, subject: Schema.String, text: Schema.String }),
  ),
  person: Schema.optionalKey(
    Schema.Struct({
      // An invitee with no Neucleos account yet has no actor id (P1c: null).
      actorId: Schema.optionalKey(Schema.NullOr(Schema.String)),
      displayName: Schema.optionalKey(Schema.String),
      invitedAddress: Schema.optionalKey(Schema.String),
    }),
  ),
  world: Schema.optionalKey(Schema.Struct({ worldKey: text, name: Schema.String })),
  appliedAt: Schema.optionalKey(Schema.Number),
  approvalKey: Schema.optionalKey(Schema.String),
  // cc-platform #1335: why a refused change was refused, present only on a refused change that has one. Any code is
  // read: those this Desk knows get their own words, any other the general refusal (gateInstancesClient).
  refusalCode: Schema.optionalKey(text),
});
export type MemberChange = typeof change.Type;
// A field this app does not know is refused, never dropped: a newer card must not show as if complete here.
const decodeShape = Schema.decodeUnknownSync(change, { onExcessProperty: "error" });
/** The label of the line that ends every card, the World's own words for what approving does. */
export const WHEN_YOU_APPROVE = "When you approve";
const SENDS_EMAIL = new Set<MemberChange["kind"]>(["invite-email", "resend"]);
export const APPLY_MEMBER_CHANGE_TOOL = "neucleos_apply_member_change";
export const MEMBER_CHANGE_HEADLINE = "Change who belongs to this World";

/**
 * The stored change as a card, or a throw when it cannot be shown whole: a blank title, a time that is no date,
 * no lines or no "When you approve" line last, or an email missing from (or added to) a kind that does (not) send one.
 */
export function decodeMemberChange(value: unknown): MemberChange {
  const decoded = decodeShape(value);
  const last = decoded.lines.at(-1);
  if (
    decoded.title.trim() === "" ||
    // A time that is no date (the Date range is ±8.64e15 ms).
    !(Number.isFinite(decoded.expiresAt) && Math.abs(decoded.expiresAt) <= 8.64e15) ||
    last === undefined ||
    last[0] !== WHEN_YOU_APPROVE ||
    decoded.lines.some(([label, words]) => label.trim() === "" || words.trim() === "") ||
    SENDS_EMAIL.has(decoded.kind) !== (decoded.email !== undefined) ||
    // world-member-change-v2: a refusal code is present only on a refused change.
    (decoded.refusalCode !== undefined && decoded.status !== "refused")
  )
    throw new Error("incomplete member change");
  return decoded;
}

const whenOf = (ms: number) =>
  new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(ms);

/**
 * The card's rows: every line the World stored, in its order and words (its last line says what approving does),
 * then the exact message when one is sent, the change the approval is bound to and when the card expires.
 */
export function memberChangeRows(value: MemberChange): ReadonlyArray<readonly [string, string]> {
  return [
    ...value.lines.map(([label, words]) => [label, words] as const),
    ...(value.email
      ? ([
          ["Email to", value.email.to],
          ["Subject", value.email.subject],
          ["Message", value.email.text],
        ] as const)
      : []),
    ["Change", `${value.changeKey} · digest ${value.digest.slice(0, 12)}`] as const,
    ["Card expires", whenOf(value.expiresAt)] as const,
  ];
}

/** Why a stored change that is no longer waiting cannot be approved, by its status. */
export const MEMBER_CHANGE_STATUS_WORDS: Readonly<
  Record<Exclude<MemberChange["status"], "proposed">, string>
> = {
  dispatching:
    "This change is already being made; there is nothing left to approve. Ask Cleo what it did.",
  applied: "This change was already made; there is nothing left to approve.",
  refused: "Neucleos refused this change, and nothing was changed. Ask Cleo to prepare it again.",
  expired: "This prepared change has expired. Ask Cleo to prepare it again.",
  unknown:
    "This change was started and its outcome is not read back yet, so it may have happened. Ask Cleo to read the members before trying again.",
};

/**
 * W-dd5db5e2: the kinds that add, remove or change the role of a member. The World approves them only with the person's
 * Touch ID on the change's Members card on their Mac (backend lib/memberPresence.js MEMBER_PRESENCE_KINDS, the Desk's
 * server memberTouchIdApproval.ts MEMBER_PRESENCE_KINDS).
 */
export const MEMBER_TOUCH_ID_KINDS: ReadonlySet<string> = new Set([
  "invite-link",
  "invite-email",
  "restore",
  "revoke",
  "remove",
  "move",
  "permissions",
]);

/**
 * W-786fe139: why a card shown through a conversation's door (Pocket, or a window with no Mac) cannot approve such a change:
 * the World refuses that approval (GATE_PRESENCE_REQUIRED) until Pocket can ask for Face ID. Said before Approve, not after.
 */
export const MEMBER_CHANGE_TOUCH_ID_ON_MAC =
  "This changes who belongs to your World, so it is approved only with Touch ID on its Members card in Cleo on your Mac. You can decline it here.";
/** The Desk conversation opens this card; the press itself is never a decision. */
export const MEMBERS_CARD_TOUCH_ID = "Approve this on its Members card; it asks for Touch ID.";

export interface MemberChangeCard {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
  /** Why Approve is off, or null when the card shows exactly the change the call names and it still waits. */
  readonly blocked: string | null;
  /** A valid proposed change whose approval requires Touch ID on its Members card. */
  readonly approveOnCard?: true;
}

/**
 * Can 47: the Members card for an apply call (its arguments) and the stored change the World gave with its approval. Approve is
 * blocked, with the reason, unless the call names exactly this change (its key and digest, nothing else), the change can be shown
 * whole, it still waits and it has not expired: as the Desk's card.
 */
export function memberChangeCard(
  args: Record<string, unknown>,
  stored: unknown,
  now: number,
): MemberChangeCard {
  const blocked = (headline: string, why: string): MemberChangeCard => ({
    headline,
    rows: [["Why you cannot approve yet", why]],
    blocked: why,
  });
  const { changeKey, digest } = args;
  if (typeof changeKey !== "string" || typeof digest !== "string")
    return blocked(
      MEMBER_CHANGE_HEADLINE,
      "This request does not name a prepared change, so it cannot be shown or approved.",
    );
  if (Object.keys(args).some((name) => name !== "changeKey" && name !== "digest"))
    return blocked(
      MEMBER_CHANGE_HEADLINE,
      "This request carries more than the prepared change it names, so it cannot be approved. Ask Cleo to prepare it again.",
    );
  let value: MemberChange;
  try {
    value = decodeMemberChange(stored);
  } catch {
    return blocked(
      "Cannot show this change",
      "Neucleos did not send this change in a form this app can show whole, so it cannot be approved here.",
    );
  }
  if (value.changeKey !== changeKey || value.digest !== digest)
    return blocked(
      "Cannot show this change",
      "The prepared change is not the one this request names. Ask Cleo to prepare it again.",
    );
  if (value.status !== "proposed")
    return blocked(value.title, MEMBER_CHANGE_STATUS_WORDS[value.status]);
  if (value.expiresAt <= now)
    return blocked("Cannot show this change", MEMBER_CHANGE_STATUS_WORDS.expired);
  // W-786fe139: the change is shown whole, and where it is approved is said before anyone presses Approve.
  if (MEMBER_TOUCH_ID_KINDS.has(value.kind))
    return {
      headline: value.title,
      rows: [...memberChangeRows(value), ["Where you approve it", MEMBER_CHANGE_TOUCH_ID_ON_MAC]],
      blocked: MEMBER_CHANGE_TOUCH_ID_ON_MAC,
      approveOnCard: true,
    };
  return { headline: value.title, rows: memberChangeRows(value), blocked: null };
}
